import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { FileSpreadsheet, Download, Loader2, ShieldCheck, Database, Mail, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { downloadFile } from '../utils/download';

const API = '';

const reports = [
  {
    id: 'po-wise',
    title: 'PO-wise Report',
    description: 'Complete PO details with SKU-level ordered, dispatched, and balance quantities',
    endpoint: '/api/reports/po-wise',
    filename: 'po_wise_report.xlsx'
  },
  {
    id: 'dispatch-history',
    title: 'Dispatch History',
    description: 'All dispatch entries with challan numbers, dates, and quantities',
    endpoint: '/api/reports/dispatch-history',
    filename: 'dispatch_history.xlsx'
  },
  {
    id: 'ageing',
    title: 'Ageing Report',
    description: 'Pending POs with ageing analysis and criticality status',
    endpoint: '/api/reports/ageing',
    filename: 'ageing_report.xlsx'
  }
];

export function Reports() {
  const [downloading, setDownloading] = useState(null);
  const [backupLogs, setBackupLogs] = useState([]);
  const [backupSettings, setBackupSettings] = useState(null);
  const [sendingBackup, setSendingBackup] = useState(false);
  const [prodFilters, setProdFilters] = useState({ from_date: '', to_date: '', po_number: '', worker_id: '' });
  const [pos, setPOs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/api/backup/logs`, { withCredentials: true })
        .then(({ data }) => setBackupLogs(data))
        .catch(() => {});
      axios.get(`${API}/api/backup/settings`, { withCredentials: true })
        .then(({ data }) => setBackupSettings(data))
        .catch(() => {});
    }
    // PO + worker dropdowns for production-report filters
    axios.get(`${API}/api/po?limit=1000`, { withCredentials: true })
      .then(({ data }) => setPOs(data.pos || []))
      .catch(() => {});
    axios.get(`${API}/api/job-workers`, { withCredentials: true })
      .then(({ data }) => setWorkers(data || []))
      .catch(() => {});
  }, [isAdmin]);

  const buildProdReportUrl = () => {
    const qs = new URLSearchParams();
    Object.entries(prodFilters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    const q = qs.toString();
    return `/api/reports/production${q ? `?${q}` : ''}`;
  };

  // Weekly Wages Report (job-card-wise wages per worker, date range)
  // Initialize with current week's Monday → Sunday range
  const _getCurrentWeek = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return [monday.toISOString().split('T')[0], sunday.toISOString().split('T')[0]];
  };
  const [_initFrom, _initTo] = _getCurrentWeek();
  const [weeklyFrom, setWeeklyFrom] = useState(_initFrom);
  const [weeklyTo, setWeeklyTo] = useState(_initTo);
  const buildWeeklyReportUrl = () => {
    const qs = new URLSearchParams({ date_from: weeklyFrom, date_to: weeklyTo });
    return `/api/wages/weekly-report/download?${qs.toString()}`;
  };

  const downloadReport = async (report) => {
    setDownloading(report.id);
    
    const result = await downloadFile(report.endpoint, report.filename, true);
    
    if (result.success) {
      toast.success(`${report.title} downloaded`);
    } else {
      toast.error(`Failed to download ${report.title}`);
    }
    
    setDownloading(null);
  };

  // Job Card reports (dispatch pendency, ageing, day-wise stage)
  const [dayFrom, setDayFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; });
  const [dayTo, setDayTo] = useState(() => new Date().toISOString().split('T')[0]);
  const jcReports = [
    { id: 'jc-dispatch-pendency', title: 'JC Dispatch Pendency', description: 'Job cards that reached Dispatch stage but still have pairs pending — SKU-wise with stage-wise output of the pending pairs', endpoint: '/api/reports/jc-dispatch-pendency', filename: 'jc_dispatch_pendency.xlsx', iconBg: 'bg-rose-100', iconColor: 'text-rose-700' },
    { id: 'jc-ageing', title: 'Job Card Ageing', description: 'Which job card is stuck at which stage and for how many days, plus a stage-wise timeline per job card', endpoint: '/api/reports/jc-ageing', filename: 'jc_ageing_report.xlsx', iconBg: 'bg-violet-100', iconColor: 'text-violet-700' },
    { id: 'uppers-stock', title: 'Uppers in Stock', description: 'Pairs received from Closing but not yet issued to Lasting — SKU-wise with JC, PO, buyer, closing worker, dates and days in stock', endpoint: '/api/reports/uppers-stock', filename: 'uppers_in_stock.xlsx', iconBg: 'bg-orange-100', iconColor: 'text-orange-700' },
    { id: 'daywise-stage', title: 'Day-wise Stage Report', description: 'Pairs produced each day at each stage (Date × Stage matrix), stage totals and entry-level detail', endpoint: `/api/reports/daywise-stage?from_date=${dayFrom}&to_date=${dayTo}`, filename: `daywise_stage_${dayFrom}_to_${dayTo}.xlsx`, iconBg: 'bg-sky-100', iconColor: 'text-sky-700', dated: true },
  ];

  const downloadBackup = async (format) => {
    const id = `backup-${format}`;
    setDownloading(id);
    const endpoint = format === 'json' ? '/api/backup/json' : '/api/backup/excel';
    const filename = format === 'json' ? 'full_backup.json' : 'full_backup.xlsx';
    
    const result = await downloadFile(endpoint, filename, true);
    
    if (result.success) {
      toast.success(`Full backup (${format.toUpperCase()}) downloaded`);
    } else {
      toast.error(`Failed to download backup`);
    }
    
    setDownloading(null);
  };

  const sendBackupNow = async () => {
    setSendingBackup(true);
    try {
      await axios.post(`${API}/api/backup/send-now`, {}, { withCredentials: true });
      toast.success(`Backup email sent to ${backupSettings?.email || ''}`);
      const { data } = await axios.get(`${API}/api/backup/logs`, { withCredentials: true });
      setBackupLogs(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send backup');
    } finally {
      setSendingBackup(false);
    }
  };

  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const sendWhatsAppNow = async () => {
    setSendingWhatsApp(true);
    try {
      await axios.post(`${API}/api/backup/send-whatsapp`, {}, { withCredentials: true });
      toast.success(`WhatsApp report sent to ${backupSettings?.whatsapp_to || ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  return (
    <div className="p-6" data-testid="reports-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl tracking-tighter text-[#09090B]">Reports</h1>
        <p className="text-sm text-zinc-500">Download Excel reports for analysis and sharing</p>
      </div>

      {/* Production Report (Comprehensive) */}
      <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="report-card-production">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-amber-100 flex items-center justify-center">
            <FileSpreadsheet size={20} className="text-amber-700" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg text-[#09090B]">Production Report (Comprehensive)</h3>
            <p className="text-xs text-zinc-500">5 sheets — Job Card Tracker, Stage-wise Entries, Worker Performance, PO × Stage Matrix, Loss Analysis</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">From Date</label>
            <input type="date" value={prodFilters.from_date} onChange={(e) => setProdFilters(p => ({ ...p, from_date: e.target.value }))} className="h-8 px-2 border border-zinc-300 rounded-sm w-full text-xs" data-testid="prod-from-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">To Date</label>
            <input type="date" value={prodFilters.to_date} onChange={(e) => setProdFilters(p => ({ ...p, to_date: e.target.value }))} className="h-8 px-2 border border-zinc-300 rounded-sm w-full text-xs" data-testid="prod-to-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">PO (optional)</label>
            <select value={prodFilters.po_number} onChange={(e) => setProdFilters(p => ({ ...p, po_number: e.target.value }))} className="h-8 px-2 border border-zinc-300 rounded-sm w-full text-xs bg-white" data-testid="prod-po-filter">
              <option value="">All POs</option>
              {pos.map(p => <option key={p.po_number} value={p.po_number}>{p.po_number}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Worker (optional)</label>
            <select value={prodFilters.worker_id} onChange={(e) => setProdFilters(p => ({ ...p, worker_id: e.target.value }))} className="h-8 px-2 border border-zinc-300 rounded-sm w-full text-xs bg-white" data-testid="prod-worker-filter">
              <option value="">All Workers</option>
              {workers.map(w => <option key={w.id || w._id} value={w.id || w._id}>{w.name}</option>)}
            </select>
          </div>
        </div>

        <a
          href={buildProdReportUrl()}
          download
          className="inline-flex items-center justify-center h-9 px-4 rounded-sm text-sm border border-amber-200 bg-amber-600 hover:bg-amber-700 text-white no-underline"
          data-testid="download-production-report"
        >
          <Download size={14} className="mr-2" /> Download Production Report
        </a>
      </div>

      {/* Weekly Wages Report (new) */}
      <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="report-card-weekly-wages">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center">
            <FileSpreadsheet size={20} className="text-emerald-700" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg text-[#09090B]">Weekly Wages Report</h3>
            <p className="text-xs text-zinc-500">Job-card-wise wages and worker breakdown for the selected date range. 2 sheets: detailed entries + worker summary.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">From Date</label>
            <input
              type="date"
              value={weeklyFrom}
              onChange={(e) => setWeeklyFrom(e.target.value)}
              className="h-8 px-2 border border-zinc-300 rounded-sm w-full text-xs"
              data-testid="weekly-from-date"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">To Date</label>
            <input
              type="date"
              value={weeklyTo}
              onChange={(e) => setWeeklyTo(e.target.value)}
              className="h-8 px-2 border border-zinc-300 rounded-sm w-full text-xs"
              data-testid="weekly-to-date"
            />
          </div>
        </div>

        <a
          href={buildWeeklyReportUrl()}
          download
          className={`inline-flex items-center justify-center h-9 px-4 rounded-sm text-sm border border-emerald-200 bg-emerald-600 hover:bg-emerald-700 text-white no-underline ${(!weeklyFrom || !weeklyTo) ? 'opacity-50 pointer-events-none' : ''}`}
          data-testid="download-weekly-wages-report"
        >
          <Download size={14} className="mr-2" /> Download Weekly Wages Report
        </a>
      </div>

      {/* Job Card Reports */}
      <div className="mb-6" data-testid="jc-reports-section">
        <h2 className="font-heading font-bold text-xl mb-3 text-[#09090B]">Job Card Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {jcReports.map(report => (
            <div key={report.id} className="bg-white border border-zinc-200 p-6 flex flex-col" data-testid={`report-card-${report.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 ${report.iconBg} flex items-center justify-center`}>
                  <FileSpreadsheet size={20} className={report.iconColor} />
                </div>
                <h3 className="font-heading font-bold text-lg text-[#09090B]">{report.title}</h3>
              </div>
              <p className="text-sm text-zinc-500 flex-1 mb-4">{report.description}</p>
              {report.dated && (
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">From Date</label>
                    <input type="date" value={dayFrom} onChange={(e) => setDayFrom(e.target.value)} className="h-8 px-2 border border-zinc-300 rounded-sm w-full text-xs" data-testid="daywise-from-date" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">To Date</label>
                    <input type="date" value={dayTo} onChange={(e) => setDayTo(e.target.value)} className="h-8 px-2 border border-zinc-300 rounded-sm w-full text-xs" data-testid="daywise-to-date" />
                  </div>
                </div>
              )}
              <Button
                onClick={() => downloadReport(report)}
                disabled={downloading === report.id}
                variant="outline"
                className="w-full h-9 rounded-sm"
                data-testid={`download-${report.id}`}
              >
                {downloading === report.id ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" />Downloading...</>
                ) : (
                  <><Download size={14} className="mr-2" />Download Excel</>
                )}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reports.map(report => (
          <div
            key={report.id}
            className="bg-white border border-zinc-200 p-6 flex flex-col"
            data-testid={`report-card-${report.id}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center">
                <FileSpreadsheet size={20} className="text-zinc-600" />
              </div>
              <h3 className="font-heading font-bold text-lg text-[#09090B]">{report.title}</h3>
            </div>
            
            <p className="text-sm text-zinc-500 flex-1 mb-4">{report.description}</p>
            
            <Button
              onClick={() => downloadReport(report)}
              disabled={downloading === report.id}
              variant="outline"
              className="w-full h-9 rounded-sm"
              data-testid={`download-${report.id}`}
            >
              {downloading === report.id ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download size={14} className="mr-2" />
                  Download Excel
                </>
              )}
            </Button>
          </div>
        ))}
      </div>

      {/* Recent Challans Section */}
      <div className="mt-8">
        <h2 className="font-heading font-bold text-xl mb-4 text-[#09090B]">Quick Links</h2>
        <div className="bg-white border border-zinc-200 p-4">
          <p className="text-sm text-zinc-500 mb-3">
            Individual challan PDFs can be downloaded from the Dispatch Entry page after creating a dispatch.
          </p>
          <Button
            variant="outline"
            className="h-9 rounded-sm"
            onClick={() => window.location.href = '/dispatch/new'}
          >
            Go to Dispatch Entry
          </Button>
        </div>
      </div>

      {/* Data Backup Section - Admin Only */}
      {isAdmin && (
        <div className="mt-8" data-testid="backup-section">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={20} className="text-green-600" />
            <h2 className="font-heading font-bold text-xl text-[#09090B]">Data Backup</h2>
            <span className="text-[10px] uppercase tracking-wider bg-green-50 border border-green-200 text-green-700 px-2 py-0.5 rounded-sm">Admin Only</span>
          </div>
          <div className="bg-white border border-zinc-200 p-6">
            <p className="text-sm text-zinc-600 mb-4">
              Download a complete backup of all your data — Purchase Orders, Dispatches, Challans, and Buyers. Keep your data safe.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-zinc-200 p-4 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Database size={16} className="text-blue-600" />
                  <h3 className="font-bold text-sm text-[#09090B]">Excel Backup</h3>
                </div>
                <p className="text-xs text-zinc-500 mb-3">
                  All data in a structured Excel workbook with separate sheets for POs, Dispatches, Challans, and Buyers. Ready for analysis.
                </p>
                <Button
                  onClick={() => downloadBackup('excel')}
                  disabled={downloading === 'backup-excel'}
                  variant="outline"
                  className="w-full h-9 rounded-sm"
                  data-testid="download-backup-excel"
                >
                  {downloading === 'backup-excel' ? (
                    <><Loader2 size={14} className="mr-2 animate-spin" />Exporting...</>
                  ) : (
                    <><Download size={14} className="mr-2" />Download Excel Backup</>
                  )}
                </Button>
              </div>
              <div className="border border-zinc-200 p-4 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Database size={16} className="text-amber-600" />
                  <h3 className="font-bold text-sm text-[#09090B]">JSON Backup</h3>
                </div>
                <p className="text-xs text-zinc-500 mb-3">
                  Complete raw database export in JSON format. Use this to restore data or migrate to another system.
                </p>
                <Button
                  onClick={() => downloadBackup('json')}
                  disabled={downloading === 'backup-json'}
                  variant="outline"
                  className="w-full h-9 rounded-sm"
                  data-testid="download-backup-json"
                >
                  {downloading === 'backup-json' ? (
                    <><Loader2 size={14} className="mr-2 animate-spin" />Exporting...</>
                  ) : (
                    <><Download size={14} className="mr-2" />Download JSON Backup</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Backup Scheduler Section - Admin Only */}
      {isAdmin && (
        <div className="mt-8" data-testid="email-backup-section">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={20} className="text-blue-600" />
            <h2 className="font-heading font-bold text-xl text-[#09090B]">Automated Email Backup</h2>
            <span className="text-[10px] uppercase tracking-wider bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5 rounded-sm">Daily</span>
          </div>
          <div className="bg-white border border-zinc-200 p-6">
            {/* Schedule Info */}
            {backupSettings && (
              <div className="flex items-center gap-6 mb-4 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-zinc-500" />
                  <span className="text-sm text-zinc-700">
                    Daily at <strong>{String(backupSettings.time_hour).padStart(2,'0')}:{String(backupSettings.time_minute).padStart(2,'0')} IST</strong>
                  </span>
                </div>
                <div className="text-sm text-zinc-700">
                  Email: <strong>{backupSettings.email}</strong>
                </div>
                {backupSettings.whatsapp_to && (
                  <div className="text-sm text-zinc-700">
                    WhatsApp: <strong>{backupSettings.whatsapp_to}</strong>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              <Button
                onClick={sendBackupNow}
                disabled={sendingBackup}
                className="h-9 rounded-sm bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="send-backup-now"
              >
                {sendingBackup ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Mail size={14} className="mr-2" />Send Email Backup</>
                )}
              </Button>
              {backupSettings?.whatsapp_enabled && (
                <Button
                  onClick={sendWhatsAppNow}
                  disabled={sendingWhatsApp}
                  className="h-9 rounded-sm bg-green-600 hover:bg-green-700 text-white"
                  data-testid="send-whatsapp-now"
                >
                  {sendingWhatsApp ? (
                    <><Loader2 size={14} className="mr-2 animate-spin" />Sending...</>
                  ) : (
                    <>Send WhatsApp Report</>
                  )}
                </Button>
              )}
              <span className="text-xs text-zinc-500">Sends immediately</span>
            </div>

            {/* Backup Log */}
            {backupLogs.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-zinc-700 mb-2">Recent Backup History</h3>
                <div className="border border-zinc-200 rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500">Date</th>
                        <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500">Status</th>
                        <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500">Records</th>
                        <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500">Sent To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backupLogs.slice(0, 10).map((log, i) => (
                        <tr key={i} className="border-t border-zinc-200">
                          <td className="p-2 font-mono text-xs">
                            {new Date(log.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                          </td>
                          <td className="p-2">
                            {log.status === 'success' ? (
                              <span className="flex items-center gap-1 text-green-600 text-xs"><CheckCircle size={12} /> Sent</span>
                            ) : (
                              <span className="flex items-center gap-1 text-red-500 text-xs"><XCircle size={12} /> Failed</span>
                            )}
                          </td>
                          <td className="p-2 text-xs text-zinc-600">
                            {log.stats ? Object.entries(log.stats).map(([k,v]) => `${v} ${k}`).join(', ') : '—'}
                          </td>
                          <td className="p-2 text-xs text-zinc-600">{log.recipient}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
